import { z } from 'zod';
import { visitDetailDTOSchema, visitDTOSchema, hostSummaryDTOSchema, monthlyVisitUsageDTOSchema, visitListItemDTOSchema, } from '@instanexus/contracts';
import { dataEnvelopeSchema, DEVICE_API_PREFIX, listEnvelopeSchema, WEB_API_PREFIX, } from '../transport.js';
// `{ data }` with no `meta` — see `VisitsResource.searchHosts` for why, and for
// what went wrong when this was a `listEnvelopeSchema`.
const hostListSchema = dataEnvelopeSchema(z.array(hostSummaryDTOSchema));
const visitListSchema = listEnvelopeSchema(visitListItemDTOSchema);
const singleVisitSchema = dataEnvelopeSchema(visitDTOSchema);
const singleVisitDetailSchema = dataEnvelopeSchema(visitDetailDTOSchema);
const monthlyUsageSchema = dataEnvelopeSchema(monthlyVisitUsageDTOSchema);
export function createVisitsResource(transport) {
    return {
        async searchHosts(query) {
            return transport.request({
                path: `${DEVICE_API_PREFIX}/visits/hosts`,
                schema: hostListSchema,
                query: { q: query.q },
                auth: 'device',
            });
        },
        async checkIn(input) {
            const envelope = await transport.request({
                method: 'POST',
                path: `${DEVICE_API_PREFIX}/visits`,
                schema: singleVisitSchema,
                body: input,
                auth: 'device',
            });
            return envelope.data;
        },
        async list(query) {
            return transport.request({
                path: `${WEB_API_PREFIX}/visits`,
                schema: visitListSchema,
                query: {
                    page: query?.page,
                    pageSize: query?.pageSize,
                    sort: query?.sort,
                    status: query?.status,
                    locationId: query?.locationId,
                    from: query?.from,
                    to: query?.to,
                    q: query?.q,
                },
            });
        },
        async get(id) {
            const envelope = await transport.request({
                path: `${WEB_API_PREFIX}/visits/${encodeURIComponent(id)}`,
                schema: singleVisitDetailSchema,
            });
            return envelope.data;
        },
        async checkOut(id) {
            const envelope = await transport.request({
                method: 'POST',
                path: `${WEB_API_PREFIX}/visits/${encodeURIComponent(id)}/check-out`,
                schema: singleVisitSchema,
            });
            return envelope.data;
        },
        async exportCsv(query) {
            return transport.requestText({
                path: `${WEB_API_PREFIX}/visits/export`,
                accept: 'text/csv',
                query: {
                    status: query?.status,
                    locationId: query?.locationId,
                    from: query?.from,
                    to: query?.to,
                    q: query?.q,
                },
            });
        },
        async getPhoto(id) {
            return transport.requestBlob({
                path: `${WEB_API_PREFIX}/visits/${encodeURIComponent(id)}/photo`,
                // `image/*` rather than the exact allow-list: the route ignores `accept` entirely
                // and answers with the stored row's own content type, so naming the three members of
                // `VISITOR_PHOTO_CONTENT_TYPES` here would create a second place that has to be kept
                // in step with the upload allow-list for no behavioural gain.
                accept: 'image/*',
            });
        },
        async getSignedAgreementPdf(visitId, documentId) {
            return transport.requestBlob({
                path: `${WEB_API_PREFIX}/visits/${encodeURIComponent(visitId)}/agreements/${encodeURIComponent(documentId)}/signed-pdf`,
                accept: 'application/pdf',
            });
        },
        async badgePreview(id) {
            const response = await transport.requestText({
                path: `${WEB_API_PREFIX}/visits/${encodeURIComponent(id)}/badge-preview`,
                accept: 'image/svg+xml',
            });
            return response.body;
        },
        async monthlyUsage() {
            const envelope = await transport.request({
                path: `${WEB_API_PREFIX}/visitor/usage/monthly-visits`,
                schema: monthlyUsageSchema,
            });
            return envelope.data;
        },
    };
}
//# sourceMappingURL=visits.js.map